import React, { useMemo, useState } from "react";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";

const Contact = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = {
    name: webinfo.name || "Your Brand",
    email: webinfo.email || "hello@example.com",
    phone: webinfo.phone || "+91 80000 00000",
    phonecall: webinfo.phonecall || "+918000000000",
    address: webinfo.address || "Remote-first • Serving clients worldwide",
    whatsapp: webinfo.whatsapp || "https://wa.me/918000000000",
    mapEmbed:
      webinfo.mapEmbed ||
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609915975!2d72.74109703785945!3d19.082197839145994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63a9d90e14b%3A0x838fe179b1bdc674!2sMumbai!5e0!3m2!1sen!2sin!4v1719999999999",
    socials: webinfo.socials || {
      x: "#",
      instagram: "#",
      facebook: "#",
      linkedin: "#",
    },
    hours: webinfo.hours || "Mon–Sat · 10:00–19:00 IST",
    sla: "Avg. first response in 2–4 hours",
  };

  const topics = useMemo(
    () => [
      "New project",
      "Website / Store revamp",
      "Performance marketing",
      "Automation / Dashboards",
      "Ongoing support / Retainer",
      "Other",
    ],
    []
  );

  // local form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "",
    message: "",
    consent: false,
    honey: "", // honeypot
  });
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email.";
    if (!form.message.trim()) return "Tell us a bit about your goals.";
    if (!form.consent) return "Please agree to be contacted.";
    if (form.honey) return "Spam detected.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "idle", msg: "" });

    const err = validate();
    if (err) return setStatus({ type: "error", msg: err });

    try {
      setStatus({ type: "loading", msg: "Sending your message…" });

      // ====== Integrate your backend here ======
      // await fetch("/api/contact", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });

      await new Promise((r) => setTimeout(r, 900));

      setStatus({
        type: "success",
        msg:
          "Thanks for reaching out. Your message is in—we usually reply within a few hours on business days.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        topic: "",
        message: "",
        consent: false,
        honey: "",
      });
    } catch (er) {
      setStatus({
        type: "error",
        msg: "Something went wrong. Please try again or use WhatsApp for quicker support.",
      });
    }
  };

  return (
    <section className="c2" aria-label="Contact section">
      {/* Background accents */}
      <div className="c2-bg" aria-hidden="true" />

      <div className="c2-wrap">
        {/* Header */}
        <header className="c2-head">
          <p className="c2-kicker">Contact</p>
          <h1>Let’s talk about your next project.</h1>
          <p className="c2-sub">
            Share where you are today and where you’d like to be—we’ll respond with practical
            options, timelines, and next steps. No pressure, no generic pitches.
          </p>
        </header>

        {/* Two-column content */}
        <div className="c2-grid">
          {/* Left: Info & quick actions */}
          <aside className="c2-info">
            <div className="info-card">
              <h2>Talk to a real person</h2>
              <p>
                Prefer a quick call or message first? Reach out directly during{" "}
                <strong>{brand.hours}</strong>.
              </p>

              <ul className="contact-list">
                <li>
                  <span className="label">Call</span>
                  <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
                </li>
                <li>
                  <span className="label">Email</span>
                  <a href={`mailto:${brand.email}`}>{brand.email}</a>
                </li>
                <li>
                  <span className="label">Location</span>
                  <span>{brand.address}</span>
                </li>
              </ul>

              <div className="cta-row">
                <a
                  className="btn btn-primary"
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  Chat on WhatsApp
                </a>
                <a className="btn btn-ghost" href={`mailto:${brand.email}`}>
                  Send an email
                </a>
              </div>

              <div className="pill-row">
                <span className="pill">{brand.sla}</span>
                <span className="pill">No pushy sales calls</span>
                <span className="pill">NDA-friendly</span>
              </div>
            </div>

            <div className="info-card map-card">
              <div className="map-wrap">
                <iframe
                  title="Office Location"
                  src={brand.mapEmbed}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <small className="map-note">
                Remote-first team working across time zones. Happy to schedule video calls or screen
                shares.
              </small>
            </div>
          </aside>

          {/* Right: Form */}
          <div className="c2-form">
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <input
                type="text"
                name="honey"
                value={form.honey}
                onChange={onChange}
                className="hp"
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
              />

              <div className="row">
                <div className="field">
                  <label htmlFor="name">Full name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jane@company.com"
                    value={form.email}
                    onChange={onChange}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 9XXXX XXXXX"
                    value={form.phone}
                    onChange={onChange}
                  />
                </div>

                <div className="field">
                  <label htmlFor="topic">Topic</label>
                  <select
                    id="topic"
                    name="topic"
                    value={form.topic}
                    onChange={onChange}
                    aria-label="Select a topic"
                  >
                    <option value="">Select…</option>
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">How can we help?</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  placeholder="Tell us about your goals, timelines, approximate budget range, and any links that help us understand the project."
                  value={form.message}
                  onChange={onChange}
                  required
                />
              </div>

              <label className="check">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                />
                I agree to be contacted about my inquiry.
              </label>

              <div className="actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={status.type === "loading"}
                >
                  {status.type === "loading" ? "Sending…" : "Send message"}
                </button>
                <a
                  className="btn btn-ghost"
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  Chat instead
                </a>
              </div>

              <div
                className={`form-status ${
                  status.type === "success"
                    ? "ok"
                    : status.type === "error"
                    ? "err"
                    : ""
                }`}
                role="status"
                aria-live="polite"
              >
                {status.msg}
              </div>
            </form>

            {/* Micro-faqs / reassurance */}
            <div className="mini-faq">
              <div>
                <strong>What happens after I submit?</strong>
                <p>
                  We’ll review your note, ask follow-up questions if needed, and outline a clear,
                  no-obligation plan or quote.
                </p>
              </div>
              <div>
                <strong>How do you handle data?</strong>
                <p>
                  We only use your details to respond to this inquiry and any follow-ups. No
                  newsletters unless you explicitly ask for them.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Logos / trust strip */}
        <div className="c2-strip">
          <span>Teams we’ve collaborated with</span>
          <div className="logos">
            <i className="logo">NovaFin</i>
            <i className="logo">Shopster</i>
            <i className="logo">EduLeap</i>
            <i className="logo">Mediva</i>
            <i className="logo">StackHub</i>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
